import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    styled,
    TextField,
} from "@mui/material";
import { DatePicker, PickersActionBarAction } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { systemDateFormat } from "../../../constants";
import useDebounce from "../../../hooks/use-debounce";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { DatePickerType } from "../enum/date-picker-filters";
import {
    ISelectedFilters,
    SelectedFilterKeys,
} from "../interface/selected-filters";
import {
    dataBrowserListResetFiltersAction,
    dataBrowserListSetFiltersAction,
} from "../redux/lists.actions";
import CategoryFilter from "./category";
import CountryFilter from "./country-filter";
import DataField from "./data-field";

interface IOwnProps {
    onClear: () => void;
}
const Filter = ({ className, onClear }: IOwnProps & IWithClassName) => {
    const dispatch = useAppDispatch();
    const datePickerActions: Array<PickersActionBarAction> = ["today", "clear"];
    const DIGITS_ONLY = /\D+/g;
    const ALPHABET_ONLY = /[^A-Z_]/gi;

    const initFilters = useAppSelector(
        ({ dataBrowserList }) => dataBrowserList.filters,
    );

    const [filters, setFilters] = useState<ISelectedFilters>(initFilters);

    const [datePickerToggle, setDatePickerToggle] = useState({
        lastUpdatedFrom: false,
        lastUpdatedTo: false,
    });

    const lastUpdatedFromDayjsObj = useMemo(
        () => (filters.lastUpdatedFrom ? dayjs(filters.lastUpdatedFrom) : null),
        [filters.lastUpdatedFrom],
    );

    const lastUpdatedToDayjsObj = useMemo(
        () => (filters.lastUpdatedTo ? dayjs(filters.lastUpdatedTo) : null),
        [filters.lastUpdatedTo],
    );

    const debounceFilter = useDebounce(filters);

    const onChangeTextHandler = (
        label: SelectedFilterKeys,
        value: string | number,
    ) =>
        setFilters((filters) => ({
            ...filters,
            [label]: value,
        }));

    const setDatePickerToggleHandler = (
        label: DatePickerType,
        value: boolean,
    ) =>
        setDatePickerToggle((toggle) => ({
            ...toggle,
            [label]: value,
        }));

    const dateOnChange = (label: DatePickerType, value?: Dayjs) =>
        setFilters((filter) => ({
            ...filter,
            [label]: value ? value.format(systemDateFormat) : undefined,
        }));

    const onCheckBoxChange = (label: string, value: boolean) =>
        setFilters((filter) => ({
            ...filter,
            [label]: value,
        }));

    useEffect(() => {
        if (debounceFilter && debounceFilter !== initFilters) {
            dispatch(dataBrowserListSetFiltersAction(debounceFilter));
        }
    }, [debounceFilter]);

    useEffect(() => {
        setFilters(initFilters);
    }, [initFilters]);

    const monthChangeHandler =
        (label: DatePickerType) => (month: Dayjs | null) => {
            if (filters[label] && month) {
                dateOnChange(label, dayjs(filters[label]).month(month.month()));
            }
        };

    const handleClearFilters = () => {
        dispatch(dataBrowserListResetFiltersAction());
        onClear();
    };

    return (
        <div className={className}>
            <Grid className="row">
                <InputLabel className="form-label">Category</InputLabel>
                <FormControl>
                    <CategoryFilter />
                </FormControl>
            </Grid>
            <Grid className="row">
                <InputLabel className="form-label">List Name</InputLabel>
                <TextField
                    variant="standard"
                    onChange={({ target }) =>
                        onChangeTextHandler("listName", target.value)
                    }
                    value={filters.listName ?? ""}
                />
            </Grid>
            <Grid className="row">
                <InputLabel className="form-label">SIC</InputLabel>
                <TextField
                    variant="standard"
                    onChange={({ target }) =>
                        onChangeTextHandler(
                            "sicCode",
                            target.value.replace(DIGITS_ONLY, ""),
                        )
                    }
                    value={filters.sicCode ?? ""}
                />
            </Grid>
            <Grid className="row">
                <InputLabel className="form-label">NAICS</InputLabel>
                <TextField
                    variant="standard"
                    onChange={({ target }) =>
                        onChangeTextHandler(
                            "naicsCode",
                            target.value.replace(DIGITS_ONLY, ""),
                        )
                    }
                    value={filters.naicsCode ?? ""}
                />
            </Grid>
            <Grid className="row">
                <InputLabel className="form-label">Last Updated</InputLabel>
                <div className="from-to-row">
                    <DatePicker
                        onMonthChange={monthChangeHandler(DatePickerType.FROM)}
                        value={lastUpdatedFromDayjsObj}
                        maxDate={lastUpdatedToDayjsObj}
                        open={datePickerToggle.lastUpdatedFrom}
                        onOpen={() =>
                            setDatePickerToggleHandler(
                                DatePickerType.FROM,
                                true,
                            )
                        }
                        onClose={() =>
                            setDatePickerToggleHandler(
                                DatePickerType.FROM,
                                false,
                            )
                        }
                        slotProps={{
                            actionBar: { actions: datePickerActions },
                            textField: {
                                variant: "standard",
                                onClick: () =>
                                    setDatePickerToggleHandler(
                                        DatePickerType.FROM,
                                        true,
                                    ),
                            },
                        }}
                        onChange={(date) =>
                            dateOnChange(DatePickerType.FROM, date as Dayjs)
                        }
                    />
                    <InputLabel className="to-label">to</InputLabel>
                    <DatePicker
                        onMonthChange={monthChangeHandler(DatePickerType.TO)}
                        value={lastUpdatedToDayjsObj}
                        open={datePickerToggle.lastUpdatedTo}
                        onOpen={() =>
                            setDatePickerToggleHandler(DatePickerType.TO, true)
                        }
                        onClose={() =>
                            setDatePickerToggleHandler(DatePickerType.TO, false)
                        }
                        minDate={lastUpdatedFromDayjsObj}
                        slotProps={{
                            actionBar: { actions: datePickerActions },
                            textField: {
                                variant: "standard",
                                onClick: () =>
                                    setDatePickerToggleHandler(
                                        DatePickerType.TO,
                                        true,
                                    ),
                            },
                        }}
                        onChange={(date) =>
                            dateOnChange(DatePickerType.TO, date as Dayjs)
                        }
                    />
                </div>
            </Grid>
            <Grid className="row">
                <InputLabel className="form-label">Locations</InputLabel>
                <div className="from-to-row">
                    <TextField
                        variant="standard"
                        value={filters.recordsFrom ?? ""}
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        onChange={({ target }) =>
                            onChangeTextHandler(
                                "recordsFrom",
                                Number(target.value.replace(DIGITS_ONLY, "")),
                            )
                        }
                    />
                    <InputLabel className="to-label">-</InputLabel>
                    <TextField
                        variant="standard"
                        value={filters.recordsTo ?? ""}
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        onChange={({ target }) =>
                            onChangeTextHandler(
                                "recordsTo",
                                Number(target.value.replace(DIGITS_ONLY, "")),
                            )
                        }
                    />
                </div>
            </Grid>
            <Grid className="row">
                <InputLabel className="form-label">Country</InputLabel>
                <CountryFilter inputMask={ALPHABET_ONLY} />
            </Grid>
            <Grid className="row">
                <InputLabel className="form-label">Data Fields</InputLabel>
                <DataField inputMask={ALPHABET_ONLY} />
            </Grid>
            <Grid className="filter-container">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!filters.removeClosed}
                            onChange={({ target }) =>
                                onCheckBoxChange("removeClosed", target.checked)
                            }
                        />
                    }
                    label="Remove Closed"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!filters.removeDistributors}
                            onChange={({ target }) =>
                                onCheckBoxChange(
                                    "removeDistributors",
                                    target.checked,
                                )
                            }
                        />
                    }
                    label="Remove Distributors"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!filters.removeArchived}
                            onChange={({ target }) =>
                                onCheckBoxChange(
                                    "removeArchived",
                                    target.checked,
                                )
                            }
                        />
                    }
                    label="Remove Archived"
                />
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleClearFilters}
                    className="clear-filters-button"
                >
                    Clear Filter(s)
                </Button>
            </Grid>
        </div>
    );
};

export default styled(Filter)(({ theme }: IWithThemeProps) => ({
    display: "flex",
    flexDirection: "column",
    gap: 20,
    padding: theme?.spacing(1, 2.5),
    width: 600,
    ".row": {
        display: "grid",
        gridTemplateColumns: "100px 1fr",
    },
    ".form-label": {
        display: "flex",
        alignItems: "center",
    },
    ".MuiFormControlLabel-root": {
        justifyContent: "center",
    },
    ".MuiIconButton-root": {
        display: "none",
    },
    ".MuiStack-root .MuiTextField-root": {
        minWidth: 50,
    },
    ".to-label": {
        width: 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    ".from-to-row": {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-around",
    },
    ".MuiInputAdornment-root": {
        display: "none",
    },
    ".filter-container": {
        display: "flex",
        justifyContent: "space-between",
        label: {
            marginRight: 0,
        },
    },
    ".clear-filters-button": {
        textTransform: "none",
    },
}));
